/**
 * Brief 26a - MLCO Inbox (read-only)
 *
 * Renders the 21 first-import gap_remediation CRs grouped by missing-artefact
 * type (13 registers + 8 forms). Each item carries a "gap_remediation" tag
 * chip per Ruling 5. Click navigates to the policy reader for context.
 *
 * Scope: 26a Phase 1 only. No CR action affordances (accept/reject/defer are
 * 26b/26c).
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardCheck, AlertCircle, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import { authFetch } from "../../config/api";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function MLCOInbox() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openGap, setOpenGap] = useState(true);
  const [openRegisters, setOpenRegisters] = useState(true);
  const [openForms, setOpenForms] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const r = await authFetch(
          `${API}/api/policy/inbox?trigger=gap_remediation&status=draft`,
          { credentials: "include" }
        );
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (!cancelled) setData(j);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div data-testid="mlco-inbox-loading" className="p-6">
        <RefreshCw className="inline-block animate-spin mr-2" size={16} />
        Loading MLCO inbox...
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="mlco-inbox-error" className="p-6 text-red-700">
        <AlertCircle className="inline-block mr-2" size={16} />
        Failed to load MLCO inbox: {error}
      </div>
    );
  }
  if (!data) {
    return <div className="p-6">No data.</div>;
  }

  const items = data.items || [];
  const registers = items.filter((c) => c.missing_artefact_type === "register");
  const forms = items.filter((c) => c.missing_artefact_type === "form");
  const count = data.count || 0;

  return (
    <div data-testid="mlco-inbox-page" className="p-6 max-w-5xl mx-auto">
      <header className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ClipboardCheck size={22} />
          <h1 className="text-2xl font-semibold">MLCO Inbox</h1>
        </div>
        <span
          data-testid="mlco-inbox-total-count"
          className="inline-flex items-center px-2 py-1 rounded bg-amber-100 text-amber-900 text-sm"
        >
          {count} open
        </span>
      </header>

      <section
        data-testid="mlco-inbox-group-gap-remediation"
        className="border rounded mb-4"
      >
        <button
          type="button"
          onClick={() => setOpenGap((v) => !v)}
          className="w-full flex items-center justify-between p-3 hover:bg-gray-50"
          data-testid="mlco-inbox-group-header-gap-remediation"
        >
          <span className="flex items-center gap-2 font-semibold">
            {openGap ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            Gap Remediation ({items.length})
          </span>
          <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-900">
            gap_remediation
          </span>
        </button>

        {openGap && (
          <div className="border-t">
            <Sub
              testid="mlco-inbox-subgroup-registers"
              label={`Registers (${registers.length})`}
              open={openRegisters}
              onToggle={() => setOpenRegisters((v) => !v)}
              items={registers}
            />
            <Sub
              testid="mlco-inbox-subgroup-forms"
              label={`Forms (${forms.length})`}
              open={openForms}
              onToggle={() => setOpenForms((v) => !v)}
              items={forms}
            />
          </div>
        )}
      </section>
    </div>
  );
}

function Sub({ testid, label, open, onToggle, items }) {
  return (
    <div data-testid={testid} className="border-b last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100"
      >
        <span className="flex items-center gap-2 font-medium">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          {label}
        </span>
      </button>
      {open && (
        <ul className="divide-y">
          {items.map((c) => {
            const ref = c.missing_artefact_ref;
            const slug = c.policy_slug;
            return (
              <li
                key={c._id}
                data-testid={`mlco-inbox-item-${ref}`}
                className="p-3 hover:bg-gray-50"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono text-xs">{ref}</span>
                  <span
                    data-testid={`mlco-inbox-tag-${ref}`}
                    className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-900"
                  >
                    {c.tag}
                  </span>
                </div>
                <div className="text-sm font-medium mb-1">
                  {c.missing_artefact_name || ref}
                  <span className="text-gray-500 font-normal ml-2">
                    ({c.policy_name})
                  </span>
                </div>
                <div className="text-xs text-gray-700 mb-2 line-clamp-2">
                  {c.narrative_md}
                </div>
                <Link
                  to={`/policy/reader/${slug}?cr=${c._id}`}
                  className="text-sm text-blue-700 hover:underline"
                  data-testid={`mlco-inbox-open-${ref}`}
                >
                  Open in policy reader
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
